/**
 * RFEPanel — Phase 12: RFE Response Service
 * Standalone page at /rfe for managing USCIS Request for Evidence cases.
 */
import React, { useState, useEffect, useRef } from 'react';
import { LAUNCH } from '../lib/launch';
import { ComingSoonBanner } from './ComingSoon';
import { PurchaseNavButton } from './CheckoutFlow';
import { API_BASE } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.detail?.message || data?.detail || `HTTP ${res.status}`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return data;
};

const URGENCY_COLORS = {
  critical: 'text-red-400 bg-red-900/30 border-red-700',
  urgent:   'text-orange-400 bg-orange-900/30 border-orange-700',
  warning:  'text-yellow-400 bg-yellow-900/30 border-yellow-700',
  ok:       'text-green-400 bg-green-900/30 border-green-700',
  unknown:  'text-gray-400 bg-gray-900/30 border-gray-700',
};

function getUrgency(daysRemaining) {
  if (daysRemaining <= 7) return 'critical';
  if (daysRemaining <= 14) return 'urgent';
  if (daysRemaining <= 30) return 'warning';
  return 'ok';
}

function DeadlineBadge({ daysRemaining }) {
  const urgency = getUrgency(daysRemaining);
  const color = URGENCY_COLORS[urgency] || URGENCY_COLORS.ok;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${color}`}>
      <i className="fas fa-clock mr-1"></i>
      {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { color: 'text-yellow-400 bg-yellow-900/20 border-yellow-700', icon: 'fa-hourglass-half', label: 'Pending' },
    response_generated: { color: 'text-green-400 bg-green-900/20 border-green-700', icon: 'fa-check-circle', label: 'Response Ready' },
    response_submitted: { color: 'text-blue-400 bg-blue-900/20 border-blue-700', icon: 'fa-paper-plane', label: 'Submitted' },
    closed: { color: 'text-gray-400 bg-gray-900/20 border-gray-700', icon: 'fa-archive', label: 'Closed' },
  };
  const s = map[status] || { color: 'text-gray-400 bg-gray-900/20 border-gray-700', icon: 'fa-circle', label: status };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${s.color}`}>
      <i className={`fas ${s.icon} mr-1`}></i>{s.label}
    </span>
  );
}

// ─── Purchase Modal ──────────────────────────────────────────────────────────

function PurchaseModal({ onClose, onSuccess }) {
  const [rfeType, setRfeType] = useState('addon');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePurchase = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/api/billing/rfe-checkout?rfe_type=${rfeType}`, { method: 'POST' });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#303035]/70">
      <div className="bg-gray-900 border border-gold/30 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <h3 className="text-xl font-bold text-white mb-2">Purchase RFE Response Service</h3>
        <p className="text-gray-400 text-sm mb-6">
          Received a USCIS Request for Evidence? ImmigrateSmart generates a full response: cover letter, point-by-point reply, and evidence index.
        </p>

        <div className="space-y-3 mb-6">
          <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${rfeType === 'addon' ? 'border-gold bg-gold/5' : 'border-gray-700 hover:border-gray-500'}`}>
            <input type="radio" name="rfeType" value="addon" checked={rfeType === 'addon'} onChange={() => setRfeType('addon')} className="mt-1 accent-amber-500" />
            <div>
              <div className="font-semibold text-white">Add-On — $199</div>
              <div className="text-gray-400 text-sm">Existing ImmigrateSmart users after workflow submission (uses your original filing data)</div>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${rfeType === 'standalone' ? 'border-gold bg-gold/5' : 'border-gray-700 hover:border-gray-500'}`}>
            <input type="radio" name="rfeType" value="standalone" checked={rfeType === 'standalone'} onChange={() => setRfeType('standalone')} className="mt-1 accent-amber-500" />
            <div>
              <div className="font-semibold text-white">Standalone — $399</div>
              <div className="text-gray-400 text-sm">I filed through another service or attorney (includes full intake questionnaire)</div>
            </div>
          </label>
        </div>

        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:border-gray-400 transition-colors">
            Cancel
          </button>
          <button onClick={handlePurchase} disabled={loading || !LAUNCH.rfePurchases} className="flex-1 px-4 py-2 bg-gold text-black font-semibold rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50">
            {LAUNCH.rfePurchases ? (loading ? 'Redirecting...' : 'Continue to Payment') : 'Coming soon'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Standalone Intake Form ──────────────────────────────────────────────────

function StandaloneIntakeForm({ caseId, onSaved }) {
  const [form, setForm] = useState({ petition_type: '', filing_date: '', service_center: '', filing_summary: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch(`/api/rfe/${caseId}/intake`, {
        method: 'POST',
        body: JSON.stringify(form),
      });
      onSaved(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-3">
      <h4 className="text-white font-semibold text-sm">Intake Information</h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-gray-400 text-xs mb-1">Petition Type</label>
          <input
            type="text"
            placeholder="e.g. EB-2 NIW, H-1B"
            value={form.petition_type}
            onChange={e => setForm(f => ({ ...f, petition_type: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-gold focus:outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Filing Date</label>
          <input
            type="date"
            value={form.filing_date}
            onChange={e => setForm(f => ({ ...f, filing_date: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-gold focus:outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Service Center</label>
          <select
            value={form.service_center}
            onChange={e => setForm(f => ({ ...f, service_center: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-gold focus:outline-none"
            required
          >
            <option value="">Select...</option>
            <option>Nebraska</option>
            <option>Texas</option>
            <option>California</option>
            <option>Vermont</option>
            <option>Potomac</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-gray-400 text-xs mb-1">Filing Summary</label>
        <textarea
          placeholder="Brief description of what you originally filed..."
          value={form.filing_summary}
          onChange={e => setForm(f => ({ ...f, filing_summary: e.target.value }))}
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-gold focus:outline-none resize-none h-20"
          required
        />
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <button type="submit" disabled={loading} className="px-4 py-2 bg-gold text-black text-sm font-semibold rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50">
        {loading ? 'Saving...' : 'Save Intake Information'}
      </button>
    </form>
  );
}

// ─── Risk Assessment Card ─────────────────────────────────────────────────────

const RISK_LEVEL_STYLES = {
  low:      { color: 'text-green-400',  bg: 'bg-green-900/20',  border: 'border-green-700',  icon: 'fa-shield-alt',       label: 'Low Risk' },
  medium:   { color: 'text-yellow-400', bg: 'bg-yellow-900/20', border: 'border-yellow-700', icon: 'fa-exclamation-circle', label: 'Medium Risk' },
  high:     { color: 'text-orange-400', bg: 'bg-orange-900/20', border: 'border-orange-700', icon: 'fa-exclamation-triangle', label: 'High Risk' },
  critical: { color: 'text-red-400',    bg: 'bg-red-900/20',    border: 'border-red-700',    icon: 'fa-radiation-alt',    label: 'Critical Risk' },
};

const SEVERITY_BADGE = {
  low:    'text-green-400 bg-green-900/30 border border-green-700',
  medium: 'text-yellow-400 bg-yellow-900/30 border border-yellow-700',
  high:   'text-red-400 bg-red-900/30 border border-red-700',
};

function RiskAssessmentCard({ caseId }) {
  const [assessment, setAssessment]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [running, setRunning]         = useState(false);
  const [error, setError]             = useState('');
  const [expanded, setExpanded]       = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch(`/api/rfe/${caseId}/risk-assessment`);
        if (!cancelled) setAssessment(data);
      } catch (e) {
        // 404 means not yet run — that's fine
        if (!cancelled && !e.message.includes('404')) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  const handleRunAssessment = async () => {
    setRunning(true);
    setError('');
    try {
      const data = await apiFetch(`/api/rfe/${caseId}/assess-risk`, { method: 'POST' });
      setAssessment(data);
      setExpanded(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const style = assessment ? (RISK_LEVEL_STYLES[assessment.risk_level] || RISK_LEVEL_STYLES.medium) : null;

  return (
    <div className={`rounded-lg border ${assessment ? `${style.border} ${style.bg}` : 'border-gray-700 bg-gray-800/30'} p-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className={`fas ${assessment ? style.icon : 'fa-chart-bar'} ${assessment ? style.color : 'text-gray-400'} text-sm`}></i>
          <span className="text-sm font-semibold text-white">RFE Risk Assessment</span>
          {assessment && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${style.color} ${style.bg} border ${style.border}`}>
              {style.label} — {assessment.risk_score}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {assessment && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              {expanded ? 'Hide Details' : 'View Details'}
            </button>
          )}
          <button
            onClick={handleRunAssessment}
            disabled={running || loading}
            className="px-3 py-1 bg-blue-700 text-white text-xs font-semibold rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            <i className={`fas ${running ? 'fa-spinner fa-spin' : 'fa-sync-alt'} mr-1`}></i>
            {running ? 'Analyzing...' : assessment ? 'Re-run' : 'Run Assessment'}
          </button>
        </div>
      </div>

      {loading && (
        <p className="text-gray-500 text-xs mt-2">
          <i className="fas fa-spinner fa-spin mr-1"></i>Loading assessment...
        </p>
      )}

      {!loading && !assessment && !running && (
        <p className="text-gray-500 text-xs mt-2">
          No assessment yet. Click "Run Assessment" to analyze USCIS concerns and get risk mitigation recommendations.
        </p>
      )}

      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}

      {assessment && expanded && (
        <div className="mt-3 space-y-3">
          {/* Risk Factors */}
          {assessment.risk_factors?.length > 0 && (
            <div>
              <h5 className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">Risk Factors</h5>
              <div className="space-y-2">
                {assessment.risk_factors.map((factor, i) => (
                  <div key={i} className="bg-gray-900/60 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${SEVERITY_BADGE[factor.severity] || SEVERITY_BADGE.medium}`}>
                        {factor.severity?.toUpperCase()}
                      </span>
                      <span className="text-white text-xs font-medium">{factor.description}</span>
                    </div>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      <i className="fas fa-lightbulb text-amber-500 mr-1"></i>
                      {factor.mitigation}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {assessment.recommendations?.length > 0 && (
            <div>
              <h5 className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">Recommendations</h5>
              <ul className="space-y-1.5">
                {assessment.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
                    <i className="fas fa-check-circle text-blue-400 mt-0.5 flex-shrink-0"></i>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-gray-600 text-xs">
            Assessment generated {assessment.generated_at ? new Date(assessment.generated_at).toLocaleString() : ''}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Case Detail Card ────────────────────────────────────────────────────────

function RFECaseCard({ rfeCase, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  const [error, setError] = useState('');

  const handleActivate = async () => {
    setActivating(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/rfe/${rfeCase.id}/activate`, { method: 'POST' });
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setActivating(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/rfe/${rfeCase.id}/generate`, {
        method: 'POST',
        body: JSON.stringify({ additional_context: additionalContext || null }),
      });
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/rfe/${rfeCase.id}/download`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      alert('Response document not available or has expired.');
      return;
    }
    const text = await res.text();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rfe_response_${rfeCase.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const urgency = getUrgency(rfeCase.days_remaining);

  return (
    <div className={`bg-gray-900 border rounded-xl overflow-hidden ${urgency === 'critical' ? 'border-red-700' : urgency === 'urgent' ? 'border-orange-700' : 'border-gray-700'}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-900/30 rounded-lg flex items-center justify-center">
            <i className="fas fa-file-alt text-blue-400"></i>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold text-sm">
                RFE Case — {rfeCase.rfe_type === 'addon' ? 'Add-On' : 'Standalone'}
              </span>
              <StatusBadge status={rfeCase.status} />
            </div>
            <div className="text-gray-500 text-xs mt-0.5">
              Uploaded {rfeCase.created_at ? new Date(rfeCase.created_at).toLocaleDateString() : 'N/A'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <DeadlineBadge daysRemaining={rfeCase.days_remaining} />
          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-gray-400 text-sm`}></i>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-700 p-4 space-y-4">
          {/* Deadline */}
          <div className="flex items-center gap-4 text-sm">
            <div>
              <span className="text-gray-500">Issue Date: </span>
              <span className="text-white">{rfeCase.issue_date ? new Date(rfeCase.issue_date).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">Response Deadline: </span>
              <span className={`font-semibold ${urgency === 'critical' ? 'text-red-400' : urgency === 'urgent' ? 'text-orange-400' : 'text-white'}`}>
                {rfeCase.response_deadline ? new Date(rfeCase.response_deadline).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>

          {/* USCIS Concerns */}
          {rfeCase.uscis_concerns?.length > 0 && (
            <div>
              <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">USCIS Concerns Identified</h4>
              <ul className="space-y-1">
                {rfeCase.uscis_concerns.map((concern, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <i className="fas fa-exclamation-triangle text-yellow-500 mt-0.5 flex-shrink-0 text-xs"></i>
                    {concern}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risk Assessment */}
          <RiskAssessmentCard caseId={rfeCase.id} />

          {/* Standalone intake form */}
          {rfeCase.rfe_type === 'standalone' && !rfeCase.intake_data && rfeCase.has_active_entitlement && (
            <StandaloneIntakeForm caseId={rfeCase.id} onSaved={onUpdate} />
          )}

          {rfeCase.rfe_type === 'standalone' && rfeCase.intake_data && (
            <div className="bg-gray-800/50 rounded-lg p-3 text-sm">
              <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">Intake Information</h4>
              <div className="grid grid-cols-2 gap-2 text-gray-300">
                <div><span className="text-gray-500">Petition Type: </span>{rfeCase.intake_data.petition_type}</div>
                <div><span className="text-gray-500">Service Center: </span>{rfeCase.intake_data.service_center}</div>
                <div><span className="text-gray-500">Filing Date: </span>{rfeCase.intake_data.filing_date}</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-2">
            {/* Activate */}
            {!rfeCase.has_active_entitlement && rfeCase.status === 'pending' && (
              <button
                onClick={handleActivate}
                disabled={activating}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                <i className="fas fa-link mr-1.5"></i>
                {activating ? 'Activating...' : 'Activate (use purchased entitlement)'}
              </button>
            )}

            {/* Generate */}
            {rfeCase.has_active_entitlement && rfeCase.status === 'pending' && (
              <div className="w-full space-y-2">
                <textarea
                  placeholder="Optional: Add any additional context or notes for the response..."
                  value={additionalContext}
                  onChange={e => setAdditionalContext(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-gold focus:outline-none resize-none h-16"
                />
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="px-4 py-2 bg-gold text-black text-sm font-semibold rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50"
                >
                  <i className="fas fa-magic mr-1.5"></i>
                  {generating ? 'Generating Response...' : 'Generate RFE Response'}
                </button>
              </div>
            )}

            {/* Download */}
            {rfeCase.status === 'response_generated' && (
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-600 transition-colors"
              >
                <i className="fas fa-download mr-1.5"></i>Download Response
              </button>
            )}
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Upload Section ──────────────────────────────────────────────────────────

function UploadSection({ onUploaded }) {
  const [rfeType, setRfeType] = useState('standalone');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('rfe_type', rfeType);
      const result = await apiFetch('/api/rfe/upload', { method: 'POST', body: formData });
      onUploaded(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="bg-gray-900 border border-dashed border-gray-600 rounded-xl p-6 text-center">
      <div className="w-12 h-12 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
        <i className="fas fa-cloud-upload-alt text-blue-400 text-xl"></i>
      </div>
      <h3 className="text-white font-semibold mb-1">Upload RFE Notice</h3>
      <p className="text-gray-400 text-sm mb-4">Upload your USCIS Request for Evidence notice (PDF, DOCX, JPG, PNG — max 25MB)</p>

      <div className="flex items-center justify-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="radio" name="rfeTypeUpload" value="standalone" checked={rfeType === 'standalone'} onChange={() => setRfeType('standalone')} className="accent-amber-500" />
          Standalone ($399)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="radio" name="rfeTypeUpload" value="addon" checked={rfeType === 'addon'} onChange={() => setRfeType('addon')} className="accent-amber-500" />
          Add-On ($199)
        </label>
      </div>

      <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.tiff,.txt" onChange={handleUpload} className="hidden" id="rfe-file-input" />
      <label
        htmlFor="rfe-file-input"
        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm cursor-pointer transition-colors ${uploading ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-gold text-black hover:bg-gold/90'}`}
      >
        {uploading ? <><i className="fas fa-spinner fa-spin"></i> Uploading...</> : <><i className="fas fa-upload"></i> Choose File</>}
      </label>
      {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
    </div>
  );
}

// ─── Main RFEPanel ────────────────────────────────────────────────────────────

export default function RFEPanel() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [checkoutBanner, setCheckoutBanner] = useState(null);

  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  const fetchCases = async () => {
    if (!isLoggedIn) { setLoading(false); return; }
    try {
      const data = await apiFetch('/api/rfe');
      setCases(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCases(); }, []);

  // Stripe Checkout return handling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (!checkout) return;

    const clear = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.pathname + url.search);
    };

    if (checkout === 'canceled') {
      setCheckoutBanner({ type: 'canceled', message: 'RFE checkout was canceled. No charge was made.' });
      clear();
      return;
    }

    if (checkout === 'success') {
      setCheckoutBanner({ type: 'pending', message: 'Payment received — confirming RFE entitlement…' });
      (async () => {
        let ready = false;
        if (sessionId && isLoggedIn) {
          for (let i = 0; i < 10; i++) {
            try {
              const status = await apiFetch(
                `/api/billing/checkout/status?session_id=${encodeURIComponent(sessionId)}`
              );
              if (status.ready || status.has_rfe) {
                ready = true;
                break;
              }
            } catch {/* continue */}
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
        if (window.analytics?.trackPurchaseSuccess) {
          window.analytics.trackPurchaseSuccess('rfe', sessionId);
        }
        setCheckoutBanner({
          type: 'success',
          message: ready
            ? 'RFE purchase confirmed. Upload your notice (or activate an existing case) to continue.'
            : 'Payment received. If activation is not available yet, wait a moment and refresh the page.',
        });
        clear();
        fetchCases();
      })();
    }
  }, [isLoggedIn]);

  const handleUploaded = (newCase) => {
    setCases(prev => [newCase, ...prev]);
  };

  const handleCaseUpdate = (updatedCase) => {
    setCases(prev => prev.map(c => c.id === updatedCase.id ? updatedCase : c));
  };

  return (
    <div className="min-h-screen bg-[#3A3A3F] pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-900/30 rounded-lg flex items-center justify-center">
              <i className="fas fa-reply text-blue-400"></i>
            </div>
            <h1 className="text-3xl font-bold text-white font-cormorant">RFE Response Service</h1>
            </div>
            <PurchaseNavButton />
          </div>
          <p className="text-gray-400 mt-1">
            Received a USCIS Request for Evidence? ImmigrateSmart generates a complete, professional response — cover letter, point-by-point reply, and evidence index.
          </p>
          <ComingSoonBanner dark>
            RFE parsing and response are visible in beta. Purchases are not open on this launch.
          </ComingSoonBanner>
        </div>

        {checkoutBanner && (
          <div
            className={`mb-6 px-4 py-3 rounded-lg text-sm flex justify-between gap-3 ${
              checkoutBanner.type === 'canceled'
                ? 'bg-yellow-900/40 border border-yellow-700 text-yellow-200'
                : checkoutBanner.type === 'pending'
                  ? 'bg-blue-900/40 border border-blue-700 text-blue-200'
                  : 'bg-green-900/40 border border-green-700 text-green-200'
            }`}
          >
            <span>{checkoutBanner.message}</span>
            <button type="button" onClick={() => setCheckoutBanner(null)} className="opacity-70 hover:opacity-100">
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {!isLoggedIn ? (
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 text-center">
            <i className="fas fa-lock text-gold text-3xl mb-4"></i>
            <h3 className="text-white font-semibold text-lg mb-2">Sign in to access RFE Response</h3>
            <p className="text-gray-400 text-sm">Please sign in to upload your RFE notice and generate a response.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Purchase CTA */}
            <div className="bg-gradient-to-r from-blue-900/20 to-gray-900 border border-blue-800/40 rounded-xl p-5 flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold mb-1">Need to purchase RFE Response Service?</h3>
                <p className="text-gray-400 text-sm">Add-On $199 (existing users) · Standalone $399 (new users)</p>
              </div>
              <button
                onClick={() => setShowPurchaseModal(true)}
                disabled={!LAUNCH.rfePurchases}
                className="px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-shopping-cart mr-1.5"></i>
                {LAUNCH.rfePurchases ? 'Purchase' : 'Coming soon'}
              </button>
            </div>

            {/* Upload */}
            <UploadSection onUploaded={handleUploaded} />

            {/* Cases list */}
            <div>
              <h2 className="text-white font-semibold text-lg mb-4">My RFE Cases</h2>

              {loading ? (
                <div className="text-center py-12">
                  <i className="fas fa-spinner fa-spin text-gold text-2xl"></i>
                  <p className="text-gray-400 text-sm mt-3">Loading cases...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-400 text-sm">{error}</div>
              ) : cases.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="fas fa-inbox text-4xl mb-4 block opacity-30"></i>
                  <p>No RFE cases yet. Upload your RFE notice above to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cases.map(c => (
                    <RFECaseCard key={c.id} rfeCase={c} onUpdate={handleCaseUpdate} />
                  ))}
                </div>
              )}
            </div>

            {/* Info section */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-3">How It Works</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { icon: 'fa-upload', title: '1. Upload', desc: 'Upload your USCIS RFE notice. We extract the deadline and key concerns automatically.' },
                  { icon: 'fa-shopping-cart', title: '2. Purchase', desc: 'Choose Add-On ($199) if you used ImmigrateSmart, or Standalone ($399) to bring your own RFE.' },
                  { icon: 'fa-magic', title: '3. Generate', desc: 'Activate your purchase, add context, and generate a complete, professionally formatted response.' },
                ].map(({ icon, title, desc }) => (
                  <div key={title} className="text-center">
                    <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className={`fas ${icon} text-gold text-sm`}></i>
                    </div>
                    <div className="text-white font-semibold text-sm mb-1">{title}</div>
                    <div className="text-gray-500 text-xs">{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showPurchaseModal && (
        <PurchaseModal
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={() => setShowPurchaseModal(false)}
        />
      )}
    </div>
  );
}
